/**
 * Helper function to extract displayable content from data field
 * If data is nested (data.data), it will traverse until it finds the actual value
 * @param data - The data object to extract from
 * @param maxDepth - Maximum depth to traverse (default: 5)
 * @returns Extracted data as string or formatted JSON
 */
const extractDataContent = (data: any, maxDepth = 5): string => {
    // Handle null and undefined explicitly
    if (data === null || data === undefined) {
        return String(data);
    }
    
    // Check max depth
    if (maxDepth === 0) {
        return typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
    }
    
    // If it's a string, return it directly
    if (typeof data === 'string') {
        return data;
    }
    
    // If it's a number or boolean, convert to string
    if (typeof data === 'number' || typeof data === 'boolean') {
        return String(data);
    }
    
    // If data has nested data field (e.g., { data: { data: "string" } }), traverse it
    if (data?.data !== undefined) {
        return extractDataContent(data.data, maxDepth - 1);
    }
    
    // If it's an object, return formatted JSON
    if (typeof data === 'object') {
        try {
            return JSON.stringify(data, null, 2);
        } catch (e) {
            return String(data);
        }
    }
    
    return String(data);
};

/**
 * Renders response message from API response structures
 * Priority: data (string or JSON) > message
 * @param lastResponse - The response object from API
 * @returns Formatted string message or false if no valid content found
 */
export const renderResponseMessage = (lastResponse: any): string | false => {
    // Handle empty arrays or null/undefined
    if (!lastResponse || (Array.isArray(lastResponse) && lastResponse.length === 0)) {
        return false;
    }

    // Priority 1: Check for response.data (if not null)
    if (lastResponse?.response?.data !== undefined && lastResponse?.response?.data !== null) {
        return extractDataContent(lastResponse.response.data);
    }
    
    // Priority 2: Check for response.message
    if (lastResponse?.response?.message) {
        return lastResponse.response.message;
    }
    
    // Fallback: Check for root level message
    if (lastResponse?.message) {
        return lastResponse.message;
    }
    
    return false;
};

